import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'

import { TApi, TSupporterRoles, StateOptions, RECORD_TYPES } from '../../../../shared/const'
import { stateLabel } from '../../../../shared/helpers'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import http from '../../../../shared/http'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import useIsDirty from '../../../../shared/useIsDirty'
import { askDiscardChanges } from '../../../../shared/confirmDialog'
import { useIsReadOnly } from '../../../../shared/alumni'

import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Loading from '../../../../UI/Loading'
import RecordComments from '../../../../UI/RecordComments'
import { PlainInput, PlainTextarea, PlainSelect } from '../../../../UI/PlainField'

import styles from './style.module.scss'
import {
	formatDate as formatDateInVienna,
	formatDateTime as formatDateTimeInVienna,
	toWallClock
} from '../../../../shared/datetime'

const EMPTY = {
	title: '',
	description: '',
	category: '',
	respondBy: '',
	eventDate: '',
	location: '',
	status: 'OPEN',
	targetRoles: [],
	targetStates: []
}

const STATUS_LABEL = { DRAFT: 'Entwurf', OPEN: 'Offen', CLOSED: 'Geschlossen' }

// Reiter der Liste. ALL bleibt vorn, damit der Einstieg immer alles zeigt.
const FILTERS = [
	{ key: 'ALL', label: 'Alle' },
	{ key: 'OPEN', label: 'Offen' },
	{ key: 'DRAFT', label: 'Entwürfe' },
	{ key: 'CLOSED', label: 'Geschlossen' }
]

// Reiter im Fenster - gleiche Aufteilung wie im Unterstützer:innen-Fenster:
// erst die Sache selbst, dann wen sie erreicht, dann was zurückkam. Der Verlauf
// steht zuletzt: Er ist kein Schritt beim Bearbeiten, sondern ein
// Nachschlagewerk für die Frage „wann wurde hier was geändert".
const TABS = [
	{ key: 'data', label: 'Bedarf' },
	{ key: 'target', label: 'Zielgruppe' },
	{ key: 'responses', label: 'Rückmeldungen' },
	{ key: 'history', label: 'Kommentare' }
]

// Beschriftungen des Verlaufs. Der Server legt die rohen Feldnamen ab - benannt
// wird hier, und zwar mit denselben Wörtern wie an den Feldern daneben. Ein
// Verlauf, der andere Wörter benutzt als das Formular, zwingt zum Übersetzen
// im Kopf.
// Was für ein Ereignis der Eintrag festhält. „Ausgespielt" ist kein Bearbeiten:
// Da hat sich am Bedarf nichts geändert, er ist hinausgegangen.
const HISTORY_ACTION = {
	CREATED: 'Erfasst',
	UPDATED: 'Bearbeitet',
	NOTIFIED: 'Ausgespielt'
}

const HISTORY_LABEL = {
	title: 'Titel',
	description: 'Beschreibung',
	category: 'Kategorie',
	status: 'Status',
	respondBy: 'Rückmeldung bis',
	eventDate: 'Termin',
	location: 'Ort',
	targetRoles: 'Rollen',
	targetStates: 'Bundesländer'
}

const formatDate = value => formatDateInVienna(value)

// Im Verlauf zählt die Uhrzeit mit: An einem Tag wird ein Bedarf mehrmals
// angefasst, und die Reihenfolge ist die halbe Auskunft.
const formatDateTime = value =>
	formatDateTimeInVienna(value, {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	})

/**
 * Tage bis zum Stichtag, auf ganze Kalendertage gerundet. Negativ heißt vorbei.
 * Beide Seiten auf Mitternacht gesetzt, sonst zählt die Uhrzeit mit und
 * "heute" wäre je nach Tageszeit mal 0 und mal 1 Tag entfernt.
 */
const daysUntil = value => {
	// Beide Seiten als Wiener Wanduhrzeit: `setHours` rechnet sonst in der Zone
	// des Rechners, und die Frist wäre je nach Standort einen Tag daneben.
	const target = toWallClock(value)
	if (!target) return null
	target.setHours(0, 0, 0, 0)
	const today = toWallClock(new Date())
	today.setHours(0, 0, 0, 0)
	return Math.round((target - today) / 86400000)
}

/*
 * Was der Versand wirklich tut. Die Kurzfassung steht immer im Fenster, die
 * Feinheiten kommen per "Mehr lesen" - sie beantworten die Fragen, die sonst
 * erst im Betrieb auffallen (Wer genau? Und wann kommt keine Mail?).
 *
 * Dieselben Punkte stehen an der E-Mail-Vorlage
 * "Unterstützungsbedarf veröffentlicht" im Bereich Kommunikation.
 */
const VERSAND_DETAILS = [
	'Angeschrieben werden die aktiven Unterstützer:innen, auf die die Eingrenzung im Reiter „Zielgruppe" zutrifft: die gewählten Rollen und die gewählten Bundesländer (Heimat- oder Ziel-Bundesland). Ohne Eingrenzung sind es alle aktiven Unterstützer:innen.',
	'Kategorie, Kompetenzen und Branche steuern den Versand nicht — sie stehen nur im Bedarf selbst.',
	'Von selbst geht die E-Mail nur beim Veröffentlichen hinaus: beim Anlegen mit dem Status „Offen" oder beim Wechsel von „Entwurf" auf „Offen".',
	'Spätere Änderungen an einem offenen Bedarf lösen keine weitere E-Mail aus — auch dann nicht, wenn die Zielgruppe nachträglich erweitert wird, und ebenso wenig beim erneuten Öffnen eines geschlossenen Bedarfs. Dafür gibt es im Reiter „Zielgruppe" den Knopf „Aufruf wiederholen": Er schreibt alle passenden Personen an, die noch nicht geantwortet haben.',
	'Wer sich vorübergehend auf „nicht verfügbar" gesetzt hat, bekommt keine E-Mail und auch keine Nachricht, sobald das Profil wieder aktiv ist.',
	'Vor Ablauf der Rückmeldefrist geht keine Erinnerung hinaus; ein Aufruf lässt sich nur von Hand wiederholen.',
	'Zu- und Absagen lösen keine E-Mail an die Administration aus; sie stehen im Reiter „Rückmeldungen".'
]

/**
 * Unterstützungsbedarf — Erfassung durch die Administration (Spec Seite 14).
 *
 * Abgrenzung zu den Firmenverbindungen: Dort fragt eine Junior Company eine
 * bestimmte Person an. Hier schreibt Junior Achievement aus, und mehrere
 * Unterstützer:innen können zusagen oder absagen.
 */
export default function SupportRequests() {
	const { t } = useTranslation()
	const readOnly = useIsReadOnly()

	const [rows, setRows] = useState(null)
	const [categories, setCategories] = useState([])
	const [filter, setFilter] = useState('ALL')
	const [search, setSearch] = useState('')
	// Ein Fenster für Ansehen und Bearbeiten: { draft, tab }
	const [dialog, setDialog] = useState(null)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(dialog?.tab, tabsRef)
	const [detail, setDetail] = useState(null)
	const [history, setHistory] = useState(null)
	const [historyFailed, setHistoryFailed] = useState(false)
	// Wie viele Unterstützer:innen die gerade gesetzte Zielgruppe erreicht
	const [reach, setReach] = useState(null)
	const [reachFailed, setReachFailed] = useState(false)
	const [isSaving, setSaving] = useState(false)
	const [isNotifying, setNotifying] = useState(false)
	// Aufklapper unter dem Versand-Hinweis ("Mehr lesen")
	const [showVersandDetails, setShowVersandDetails] = useState(false)

	const load = useCallback(() => {
		http.get(TApi.ADMIN_SUPPORT_REQUESTS, { params: { limit: 100 } })
			// Alumni (#213) lesen jeden Bedarf wie einen fremden: ansehen, nicht
			// ändern — dieselbe Darstellung, die ein Landesadmin bei einem
			// Bedarf außerhalb seines Bundeslands sieht.
			.then(({ data }) =>
				setRows((data.rows || []).map(row => (readOnly ? { ...row, editable: false } : row)))
			)
			.catch(() => {
				// Meldung kommt aus shared/http
				setRows([])
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		load()
		http.get(TApi.ADMIN_SUPPORT_CATEGORIES)
			.then(({ data }) => setCategories(data || []))
			.catch(() => {
				// Ohne Kategorien bleibt das Feld frei eingebbar
			})
	}, [load])

	const openId = dialog?.draft?.id || null

	useEffect(() => {
		setDetail(null)
		setHistory(null)
		setHistoryFailed(false)
		if (!openId) return

		http.get(TApi.ADMIN_SUPPORT_REQUEST_ID(openId))
			.then(({ data }) => setDetail(data))
			.catch(() => {}) // Meldung kommt aus shared/http
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openId])

	/*
	 * Der Verlauf wird erst geholt, wenn jemand ihn aufschlägt - nicht beim
	 * Öffnen des Fensters. Er ist ein Nachschlagewerk; die meisten Bearbeitungen
	 * kommen ohne ihn aus, und dann muss auch nichts geladen werden.
	 */
	useEffect(() => {
		if (dialog?.tab !== 'history' || !openId || history !== null) return

		http.get(TApi.ADMIN_SUPPORT_REQUEST_CHANGES(openId), { disableToast: true })
			.then(({ data }) => {
				setHistory(data)
				setHistoryFailed(false)
			})
			.catch(() => setHistoryFailed(true))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialog?.tab, openId, history])

	/*
	 * Reichweite der gerade eingestellten Zielgruppe. Gefragt wird für den Stand
	 * im Fenster, nicht für den gespeicherten - sonst wüsste man beim Eingrenzen
	 * erst nach dem Speichern, wen man sich gerade wegnimmt.
	 *
	 * Mit Verzögerung, weil beim Anklicken mehrerer Merkmale sonst jede einzelne
	 * Auswahl eine Abfrage auslöst.
	 */
	const targetKey = dialog
		? JSON.stringify([dialog.draft.targetRoles || [], dialog.draft.targetStates || []])
		: null

	useEffect(() => {
		// Die Reichweite wird per POST gerechnet — für Alumni gesperrt und
		// ohne Wert, weil sie nichts ausschreiben.
		if (!targetKey || readOnly) return

		const [targetRoles, targetStates] = JSON.parse(targetKey)
		const timer = setTimeout(() => {
			http.post(
				TApi.ADMIN_SUPPORT_REQUEST_REACH,
				{ targetRoles, targetStates },
				// Die Zahl ist eine Nebenauskunft; scheitert sie, gehört an ihre
				// Stelle ein Hinweis und kein Fehlerbalken über dem Formular.
				{ disableToast: true }
			)
				.then(({ data }) => {
					setReach(data.count)
					setReachFailed(false)
				})
				.catch(() => {
					setReach(null)
					setReachFailed(true)
				})
		}, 400)

		return () => clearTimeout(timer)
	}, [targetKey, readOnly])

	/** Detail und Verlauf des offenen Bedarfs neu holen. */
	const reloadOpen = useCallback(() => {
		if (!openId) return

		http.get(TApi.ADMIN_SUPPORT_REQUEST_ID(openId))
			.then(({ data }) => setDetail(data))
			.catch(() => {}) // Meldung kommt aus shared/http

		http.get(TApi.ADMIN_SUPPORT_REQUEST_CHANGES(openId), { disableToast: true })
			.then(({ data }) => {
				setHistory(data)
				setHistoryFailed(false)
			})
			.catch(() => setHistoryFailed(true))
	}, [openId])

	const stats = useMemo(() => {
		const list = rows || []
		return {
			open: list.filter(row => row.status === 'OPEN').length,
			draft: list.filter(row => row.status === 'DRAFT').length,
			closed: list.filter(row => row.status === 'CLOSED').length,
			accepted: list.reduce((sum, row) => sum + row.accepted, 0),
			expired: list.filter(row => row.status === 'OPEN' && row.isExpired).length
		}
	}, [rows])

	const visible = useMemo(() => {
		const term = search.trim().toLowerCase()
		return (rows || []).filter(row => {
			if (filter !== 'ALL' && row.status !== filter) return false
			if (!term) return true
			return [row.title, row.category, row.location, row.description]
				.filter(Boolean)
				.some(value => value.toLowerCase().includes(term))
		})
	}, [rows, filter, search])

	const patchDraft = patch =>
		setDialog(current => ({ ...current, draft: { ...current.draft, ...patch } }))

	const toggleIn = (field, value) =>
		setDialog(current => {
			const list = current.draft[field] || []
			return {
				...current,
				draft: {
					...current.draft,
					[field]: list.includes(value)
						? list.filter(item => item !== value)
						: [...list, value]
				}
			}
		})

	const save = () => {
		const draft = dialog.draft

		if (draft.title.trim().length < 3 || !draft.category) {
			toast.warning('Titel und Kategorie sind erforderlich.')
			setDialog({ ...dialog, tab: 'data' })
			return
		}

		const payload = {
			title: draft.title,
			description: draft.description || null,
			category: draft.category,
			respondBy: draft.respondBy || null,
			eventDate: draft.eventDate || null,
			location: draft.location || null,
			status: draft.status,
			targetRoles: draft.targetRoles,
			targetStates: draft.targetStates
		}

		setSaving(true)
		const request = draft.id
			? http.put(TApi.ADMIN_SUPPORT_REQUEST_ID(draft.id), payload)
			: http.post(TApi.ADMIN_SUPPORT_REQUESTS, payload)

		request
			.then(({ data }) => {
				// Der Server meldet, wie viele benachrichtigt wurden - das ist die
				// Information, die den Admin nach dem Veroeffentlichen interessiert.
				if (data?.notified > 0) {
					toast.success(`Gespeichert. ${data.notified} Unterstützer:innen benachrichtigt.`)
				} else {
					toast.success(t('Saved successfully'))
				}
				setDialog(null)
				load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	/*
	 * Aufruf wiederholen. Angeschrieben wird nur, wer noch nicht geantwortet hat
	 * - wer zugesagt oder abgesagt hat, hat den Aufruf gesehen.
	 *
	 * Der Server geht dabei vom gespeicherten Stand aus. Wer die Zielgruppe im
	 * Fenster gerade verändert hat, muss also erst speichern; darauf weist der
	 * Rückfragetext hin.
	 *
	 * Der Rückfragetext nennt außerdem, was der Aufruf tatsächlich auslöst: eine
	 * E-Mail mit dem Betreff "Anfrage von Junior Achievement". Vorher stand dort
	 * nur, dass der Aufruf hinausgeht - wer den Versand nicht kennt, konnte daraus
	 * nicht schließen, ob überhaupt jemand etwas davon bemerkt.
	 */
	const repeatCall = async () => {
		const answered = (dialog.draft.accepted || 0) + (dialog.draft.declined || 0)
		const open = Math.max((detail?.recipientCount || 0) - answered, 0)

		const result = await Swal.fire({
			title: 'Aufruf wiederholen?',
			html: `Alle passenden Unterstützer:innen, die bisher <b>nicht</b> geantwortet haben, bekommen eine E-Mail („Anfrage von Junior Achievement") mit dem Titel des Bedarfs und einem Verweis auf den Bereich „Unterstützungsbedarf" — derzeit ${open} von ${detail?.recipientCount ?? '—'}.<br><br>Maßgeblich ist der <b>gespeicherte</b> Stand der Zielgruppe.`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Aufruf senden',
			cancelButtonText: t('Cancel'),
			confirmButtonColor: '#008b9c'
		})
		if (!result.isConfirmed) return

		setNotifying(true)
		http.post(TApi.ADMIN_SUPPORT_REQUEST_NOTIFY(dialog.draft.id), {})
			.then(({ data }) => {
				toast.success(
					data.notified
						? `Aufruf an ${data.notified} Unterstützer:innen gesendet.`
						: 'Niemand offen — es wurde keine E-Mail gesendet.'
				)
				reloadOpen()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setNotifying(false))
	}

	const remove = async row => {
		const answered = row.accepted + row.declined

		const result = await Swal.fire({
			title: row.title,
			text: answered
				? `Auf diesen Bedarf gibt es bereits ${answered} Rückmeldungen. Er wird deshalb nur geschlossen und bleibt mit den Zusagen erhalten.`
				: 'Dieser Bedarf wurde noch nicht beantwortet und wird endgültig entfernt.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: answered ? 'Schließen' : t('delete'),
			cancelButtonText: t('Cancel'),
			confirmButtonColor: answered ? '#008b9c' : '#d33'
		})
		if (!result.isConfirmed) return

		http.delete(TApi.ADMIN_SUPPORT_REQUEST_ID(row.id))
			.then(load)
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	const openDialog = (row, tab = 'data') =>
		setDialog({
			tab,
			/*
			 * Der Status beim Oeffnen - nicht der Entwurf, den der Admin verstellt.
			 * Nur der Wechsel von "Entwurf" auf "Offen" loest den Versand aus; ein
			 * erneutes Speichern eines schon offenen Bedarfs schickt nichts. Ohne
			 * diesen Vergleich stuende der Versandhinweis auch dort, wo gar keine
			 * E-Mail hinausgeht.
			 */
			savedStatus: row ? row.status : null,
			draft: row
				? {
						...row,
						// Der Server liefert leere Felder als null; ein Eingabefeld mit
						// value=null wäre unkontrolliert und warnt beim ersten Tippen.
						location: row.location || '',
						description: row.description || '',
						respondBy: row.respondBy ? row.respondBy.slice(0, 10) : '',
						eventDate: row.eventDate ? row.eventDate.slice(0, 10) : ''
				  }
				: { ...EMPTY, category: categories[0] || '' }
		})

	/*
	 * Ungespeichertes im Fenster: verglichen wird der Entwurf gegen den Stand
	 * beim Oeffnen. Der Schluessel setzt den Vergleich zurueck, wenn ein anderer
	 * Bedarf geoeffnet wird.
	 */
	const isDirty = useIsDirty(dialog?.draft, dialog ? dialog.draft.id || 'neu' : null)

	const closeDialog = async () => {
		if (isDirty && !(await askDiscardChanges())) return
		setDialog(null)
	}

	const backdropProps = useDialogDismiss(closeDialog, { enabled: Boolean(dialog) })

	/*
	 * Werte des Verlaufs lesbar machen: Der Server legt sie so ab, wie sie in
	 * der Datenbank stehen - "JURY" und "2026-08-12" beantworten die Frage
	 * nicht, "Jury-Mitglied" und "12.8.2026" schon.
	 */
	const historyValue = (field, value) => {
		if (value === null || value === undefined || (Array.isArray(value) && !value.length))
			return 'leer'

		if (field === 'respondBy' || field === 'eventDate') return formatDate(value)
		if (field === 'status') return STATUS_LABEL[value] || value

		if (Array.isArray(value)) {
			const label = {
				targetRoles: role => t(`supporterRole${role}`),
				targetStates: stateLabel
			}[field]

			return value.map(entry => (label ? label(entry) : entry)).join(' · ')
		}

		return String(value)
	}

	if (!rows) return <Loading />

	const draft = dialog?.draft
	const answeredInDialog = draft ? draft.accepted + draft.declined : 0
	/*
	 * Rückmeldungen und Verlauf zeigen nur an - dort gibt es nichts zu speichern.
	 * Ebenso ein Bedarf, der nicht nur das eigene Bundesland betrifft: Ein
	 * Landesadmin darf ihn lesen, aber nicht ändern (`editable: false` vom
	 * Server). Der Knopf soll das gar nicht erst anbieten.
	 */
	const isReadOnlyTab =
		dialog?.tab === 'responses' || dialog?.tab === 'history' || draft?.editable === false

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title="Unterstützungsbedarf" bg="#f8faff">
				<div className={styles.headerActions}>
					{!readOnly && (
						<Btn theme="primary" onClick={() => openDialog(null)}>
							Bedarf erfassen
						</Btn>
					)}
				</div>
			</MiniHeader>

			<p className={styles.intro}>
				Hier schreiben Sie einen Bedarf aus — etwa eine Jury für einen Landeswettbewerb.
				Unterstützer:innen sehen ihn in ihrem Bereich und können zusagen oder absagen. Ohne
				Einschränkung auf Rollen oder Bundesländer erreicht ein Bedarf alle aktiven
				Unterstützer:innen.
			</p>

			{/* Kennzahlen: was gerade läuft, ohne die Liste durchzugehen. */}
			<div className={styles.stats}>
				<div className={clsx(styles.stat, styles.statOpen)}>
					<span className={styles.statValue}>{stats.open}</span>
					<span className={styles.statLabel}>ausgeschrieben</span>
				</div>
				<div className={styles.stat}>
					<span className={styles.statValue}>{stats.draft}</span>
					<span className={styles.statLabel}>in Vorbereitung</span>
				</div>
				<div className={clsx(styles.stat, styles.statAccepted)}>
					<span className={styles.statValue}>{stats.accepted}</span>
					<span className={styles.statLabel}>Zusagen gesamt</span>
				</div>
				<div className={clsx(styles.stat, stats.expired && styles.statWarn)}>
					<span className={styles.statValue}>{stats.expired}</span>
					<span className={styles.statLabel}>Frist abgelaufen</span>
				</div>
			</div>

			<div className={styles.toolbar}>
				<div className={styles.filters}>
					{FILTERS.map(item => {
						const count =
							item.key === 'ALL'
								? rows.length
								: rows.filter(row => row.status === item.key).length

						return (
							<button
								key={item.key}
								type="button"
								className={clsx(
									styles.filterTab,
									filter === item.key && styles.filterTabOn
								)}
								onClick={() => setFilter(item.key)}
							>
								{item.label}
								<span className={styles.filterCount}>{count}</span>
							</button>
						)
					})}
				</div>

				<input
					type="search"
					className={styles.search}
					placeholder="Titel, Kategorie oder Ort suchen"
					value={search}
					onChange={e => setSearch(e.target.value)}
				/>
			</div>

			{!rows.length ? (
				<div className={styles.empty}>
					<p className={styles.emptyTitle}>Noch kein Bedarf erfasst.</p>
					<p className={styles.emptyText}>
						Mit „Bedarf erfassen“ schreiben Sie die erste Anfrage aus.
					</p>
				</div>
			) : !visible.length ? (
				<div className={styles.empty}>
					<p className={styles.emptyTitle}>Kein Bedarf passt zu dieser Auswahl.</p>
					<p className={styles.emptyText}>Filter oder Suchbegriff ändern.</p>
				</div>
			) : (
				<div className={styles.list}>
					{visible.map(row => {
						const answered = row.accepted + row.declined
						const remaining = row.respondBy ? daysUntil(row.respondBy) : null
						const isSoon =
							row.status === 'OPEN' &&
							!row.isExpired &&
							remaining !== null &&
							remaining <= 7

						return (
							<article
								key={row.id}
								className={clsx(styles.card, styles[`card${row.status}`])}
							>
								<div className={styles.cardMain}>
									<header className={styles.cardHead}>
										<span
											className={clsx(
												styles.status,
												styles[`status${row.status}`]
											)}
										>
											{STATUS_LABEL[row.status]}
										</span>
										<h3 className={styles.cardTitle}>{row.title}</h3>
										<span className={styles.category}>{row.category}</span>
										{row.isExpired && row.status === 'OPEN' && (
											<span className={styles.flagExpired}>
												Frist abgelaufen
											</span>
										)}
										{isSoon && (
											<span className={styles.flagSoon}>
												{remaining <= 0
													? 'Frist endet heute'
													: `noch ${remaining} Tage`}
											</span>
										)}
									</header>

									{row.description && (
										<p className={styles.excerpt}>{row.description}</p>
									)}

									{/* Termine als beschriftete Paare statt als eine graue Zeile. */}
									<dl className={styles.facts}>
										{row.respondBy && (
											<div className={styles.fact}>
												<dt>Rückmeldung bis</dt>
												<dd
													className={
														row.isExpired ? styles.expired : undefined
													}
												>
													{formatDate(row.respondBy)}
												</dd>
											</div>
										)}
										{row.eventDate && (
											<div className={styles.fact}>
												<dt>Termin</dt>
												<dd>{formatDate(row.eventDate)}</dd>
											</div>
										)}
										{row.location && (
											<div className={styles.fact}>
												<dt>Ort</dt>
												<dd>{row.location}</dd>
											</div>
										)}
										<div className={styles.fact}>
											<dt>Zielgruppe</dt>
											<dd className={styles.targetChips}>
												{row.targetRoles.length ? (
													row.targetRoles.map(role => (
														<span
															key={role}
															className={styles.targetChip}
														>
															{t(`supporterRole${role}`)}
														</span>
													))
												) : (
													<span
														className={clsx(
															styles.targetChip,
															styles.targetChipAll
														)}
													>
														Alle Rollen
													</span>
												)}
												{row.targetStates.length ? (
													row.targetStates.map(state => (
														<span
															key={state}
															className={styles.targetChip}
														>
															{stateLabel(state)}
														</span>
													))
												) : (
													<span
														className={clsx(
															styles.targetChip,
															styles.targetChipAll
														)}
													>
														Alle Bundesländer
													</span>
												)}
											</dd>
										</div>
									</dl>

									<footer className={styles.cardActions}>
										<Btn
											size="small"
											theme="secondary"
											onClick={() => openDialog(row, 'responses')}
										>
											Rückmeldungen{answered ? ` (${answered})` : ''}
										</Btn>
										<Btn
											size="small"
											theme="secondary"
											onClick={() => openDialog(row)}
										>
											{row.editable === false ? 'Ansehen' : t('edit')}
										</Btn>
										{/*
										 * Ein bereits geschlossener Bedarf laesst sich nicht
										 * noch einmal schliessen — der Knopf entfaellt dort
										 * (Anforderung #89). Ohne Rueckmeldungen bleibt das
										 * endgueltige Loeschen weiterhin moeglich.
										 *
										 * `editable: false` heißt: Der Bedarf betrifft nicht
										 * nur das eigene Bundesland. Ein Landesadmin darf ihn
										 * lesen, aber nicht schließen — die Schnittstelle
										 * weist es ohnehin ab, der Knopf soll es gar nicht
										 * erst anbieten.
										 */}
										{row.editable !== false && (row.status !== 'CLOSED' || !answered) && (
											<Btn
												size="small"
												theme="danger"
												onClick={() => remove(row)}
											>
												{answered ? 'Schließen' : t('delete')}
											</Btn>
										)}
									</footer>
								</div>

								{/*
								 * Resonanz als eigene Spalte: Zusagen sind die Zahl, auf die
								 * es ankommt, und stehen deshalb groß und nicht als Randnotiz
								 * in der Kopfzeile.
								 */}
								<aside
									className={clsx(
										styles.resonance,
										// Eine grüne 0 liest sich wie ein Erfolg - solange
										// niemand zugesagt hat, bleibt die Spalte grau.
										!row.accepted && styles.resonanceZero
									)}
								>
									<span className={styles.resonanceValue}>{row.accepted}</span>
									<span className={styles.resonanceLabel}>
										{row.accepted === 1 ? 'Zusage' : 'Zusagen'}
									</span>
									{answered > 0 && (
										<div className={styles.bar}>
											<span
												className={styles.barAccepted}
												style={{
													width: `${(row.accepted / answered) * 100}%`
												}}
											/>
										</div>
									)}
									<span className={styles.resonanceDeclined}>
										{row.declined} {row.declined === 1 ? 'Absage' : 'Absagen'}
									</span>
								</aside>
							</article>
						)
					})}
				</div>
			)}

			{dialog && (
				<div className={styles.overlay} {...backdropProps}>
					<div className={styles.modal}>
						<header className={styles.modalHead}>
							<div>
								<h2 className={styles.modalTitle}>
									{draft.id ? draft.title || 'Bedarf' : 'Bedarf erfassen'}
								</h2>
								{draft.id && (
									<span className={styles.modalSubtitle}>
										{STATUS_LABEL[draft.status]} · {draft.category}
									</span>
								)}
							</div>
							<button
								type="button"
								className={styles.close}
								onClick={closeDialog}
							>
								×
							</button>
						</header>

						<div className={styles.tabs} ref={tabsRef}>
							{TABS.map(item => {
								// Rückmeldungen und Verlauf gibt es erst, wenn der Bedarf
								// gespeichert ist.
								if (
									(item.key === 'responses' || item.key === 'history') &&
									!draft.id
								)
									return null
								// Alumni: keine Kommentare der Verwaltung.
								if (item.key === 'history' && readOnly) return null

								return (
									<button
										key={item.key}
										type="button"
										className={clsx(
											styles.tab,
											dialog.tab === item.key && styles.tabOn
										)}
										onClick={() => setDialog({ ...dialog, tab: item.key })}
									>
										{item.label}
										{item.key === 'responses' && answeredInDialog > 0 && (
											<span className={styles.tabCount}>
												{answeredInDialog}
											</span>
										)}
									</button>
								)
							})}
						</div>

						<div className={styles.body}>
							{/* Alumni (#213): alles lesbar, nichts änderbar — das fieldset sperrt
							    jedes Feld und jeden Knopf darin. */}
							<fieldset disabled={readOnly} className={styles.readOnlyFieldset}>
								{dialog.tab === 'data' && (
									<>
										<div className={styles.grid}>
											<PlainInput
												label="Titel"
												required
												className={styles.full}
												value={draft.title}
												onChange={e => patchDraft({ title: e.target.value })}
											/>
											<PlainSelect
												label="Kategorie"
												required
												placeholder="—"
												options={categories.map(c => ({
													value: c,
													label: c
												}))}
												value={draft.category}
												onChange={e => patchDraft({ category: e.target.value })}
											/>
											<PlainSelect
												label={t('status')}
												options={[
													{ value: 'DRAFT', label: 'Entwurf (nicht sichtbar)' },
													{ value: 'OPEN', label: 'Offen (wird ausgespielt)' },
													{ value: 'CLOSED', label: 'Geschlossen' }
												]}
												value={draft.status}
												onChange={e => patchDraft({ status: e.target.value })}
											/>
											<PlainInput
												label="Rückmeldung bis"
												type="date"
												value={draft.respondBy}
												onChange={e =>
													patchDraft({ respondBy: e.target.value })
												}
											/>
											<PlainInput
												label="Termin"
												type="date"
												value={draft.eventDate}
												onChange={e =>
													patchDraft({ eventDate: e.target.value })
												}
											/>
											<PlainInput
												label="Ort"
												className={styles.full}
												value={draft.location}
												onChange={e => patchDraft({ location: e.target.value })}
											/>
											<div className={styles.full}>
												<PlainTextarea
													label="Beschreibung"
													value={draft.description}
													onChange={e =>
														patchDraft({ description: e.target.value })
													}
												/>
											</div>
										</div>

										{draft.status !== 'CLOSED' && (
											<div className={styles.notice}>
												<p className={styles.noticeLead}>
													{draft.status === 'DRAFT'
														? 'Ein Entwurf wird nicht ausgespielt: Solange der Status „Entwurf" ist, sieht ihn niemand und es geht keine E-Mail hinaus.'
														: dialog.savedStatus === 'OPEN'
															? 'Der Bedarf ist bereits ausgespielt. Das Speichern schickt keine weitere E-Mail — dafür gibt es im Reiter „Zielgruppe" den Knopf „Aufruf wiederholen".'
															: 'Beim Speichern bekommen alle passenden Unterstützer:innen eine E-Mail mit dem Betreff „Anfrage von Junior Achievement".'}
												</p>

												<button
													type="button"
													className={styles.moreLink}
													onClick={() => setShowVersandDetails(open => !open)}
												>
													{showVersandDetails ? 'Weniger' : 'Mehr lesen'}
												</button>

												{showVersandDetails && (
													<ul className={styles.noticeDetails}>
														{VERSAND_DETAILS.map(line => (
															<li key={line}>{line}</li>
														))}
													</ul>
												)}
											</div>
										)}
									</>
								)}

								{dialog.tab === 'target' && (
									<>
										<p className={styles.tabHint}>
											Ohne Auswahl erreicht der Bedarf alle aktiven
											Unterstützer:innen. Jede Auswahl grenzt ein: Eine E-Mail
											bekommen nur Personen, die zu <em>allen</em> gesetzten
											Merkmalen passen.
										</p>
										{/*
										 * Was „erreichen" heißt, stand nirgends: Der Aufruf ist eine
										 * E-Mail, und sie ist nur der Anstoß — im eigenen Bereich
										 * steht der Bedarf ohnehin, solange er offen ist und zur
										 * Auswahl passt.
										 */}
										<p className={styles.tabHint}>
											Der Aufruf ist eine E-Mail mit dem Betreff „Anfrage von
											Junior Achievement" und einem Verweis auf den Bereich
											„Unterstützungsbedarf". Sie ist der Anstoß, nicht der
											einzige Weg: Wer zur Auswahl passt, sieht den Bedarf dort
											ohnehin, solange er offen ist.
										</p>

										{/*
										 * Zwei Zahlen nebeneinander: wen die Auswahl derzeit
										 * treffen würde und wen sie tatsächlich erreicht hat.
										 * Erst beide zusammen beantworten, ob ein Aufruf zu
										 * wiederholen ist.
										 */}
										<div className={styles.reach}>
											<div className={styles.reachItem}>
												<span className={styles.reachValue}>
													{reachFailed ? '—' : reach === null ? '…' : reach}
												</span>
												<span className={styles.reachLabel}>
													passen derzeit
												</span>
											</div>

											{draft.id && (
												<>
													<div className={styles.reachItem}>
														<span className={styles.reachValue}>
															{detail?.lastNotification?.recipients ?? 0}
														</span>
														<span className={styles.reachLabel}>
															{detail?.lastNotification
																? `erreicht am ${formatDate(
																		detail.lastNotification.at
																  )}`
																: 'noch nie ausgespielt'}
														</span>
													</div>
													<div className={styles.reachItem}>
														<span className={styles.reachValue}>
															{answeredInDialog}
														</span>
														<span className={styles.reachLabel}>
															haben geantwortet
														</span>
													</div>
												</>
											)}
										</div>

										{reachFailed && (
											<p className={styles.hint}>
												Die Reichweite lässt sich gerade nicht ermitteln.
											</p>
										)}

										{draft.id && draft.status === 'OPEN' && (
											<div className={styles.reachAction}>
												<Btn
													size="small"
													theme="secondary"
													disabled={isNotifying}
													onClick={repeatCall}
												>
													Aufruf wiederholen
												</Btn>
												<span className={styles.hint}>
													Schickt die E-Mail noch einmal an alle passenden
													Personen, die noch nicht geantwortet haben — auf
													Grundlage des gespeicherten Stands.
												</span>
											</div>
										)}

										<div className={styles.block}>
											<span className={styles.blockLabel}>Rollen</span>
											<div className={styles.chips}>
												{Object.values(TSupporterRoles).map(role => (
													<button
														key={role}
														type="button"
														className={clsx(
															styles.chip,
															draft.targetRoles.includes(role) &&
																styles.chipOn
														)}
														onClick={() => toggleIn('targetRoles', role)}
													>
														{t(`supporterRole${role}`)}
													</button>
												))}
											</div>
											<p className={styles.hint}>
												{draft.targetRoles.length
													? 'Nur die gewählten Rollen.'
													: 'Alle Rollen.'}
											</p>
										</div>

										<div className={styles.block}>
											<span className={styles.blockLabel}>Bundesländer</span>
											<div className={styles.chips}>
												{StateOptions.map(option => (
													<button
														key={option.value}
														type="button"
														className={clsx(
															styles.chip,
															draft.targetStates.includes(option.value) &&
																styles.chipOn
														)}
														onClick={() =>
															toggleIn('targetStates', option.value)
														}
													>
														{option.label}
													</button>
												))}
											</div>
											<p className={styles.hint}>
												{draft.targetStates.length
													? 'Nur die gewählten Bundesländer.'
													: 'Alle Bundesländer.'}
											</p>
										</div>
									</>
								)}

								{dialog.tab === 'responses' &&
									(!detail ? (
										<Loading />
									) : (
										<>
											<div className={styles.responseSummary}>
												<div className={styles.summaryItem}>
													<span className={styles.summaryValue}>
														{detail.recipientCount}
													</span>
													<span className={styles.summaryLabel}>
														ausgespielt an
													</span>
												</div>
												<div
													className={clsx(
														styles.summaryItem,
														styles.summaryAccepted
													)}
												>
													<span className={styles.summaryValue}>
														{draft.accepted}
													</span>
													<span className={styles.summaryLabel}>
														{draft.accepted === 1 ? 'Zusage' : 'Zusagen'}
													</span>
												</div>
												<div className={styles.summaryItem}>
													<span className={styles.summaryValue}>
														{draft.declined}
													</span>
													<span className={styles.summaryLabel}>
														{draft.declined === 1 ? 'Absage' : 'Absagen'}
													</span>
												</div>
											</div>

											{/*
											 * Landesadmins sehen nur die Rückmeldungen aus ihrem
											 * Bundesland. Ohne diesen Hinweis wäre unklar, warum
											 * die Zähler oben höher stehen als die Liste lang ist.
											 */}
											{detail.hiddenResponses > 0 && (
												<p className={styles.tabHint}>
													{detail.hiddenResponses === 1
														? 'Eine weitere Rückmeldung stammt aus einem anderen Bundesland und wird hier nicht angezeigt.'
														: `${detail.hiddenResponses} weitere Rückmeldungen stammen aus anderen Bundesländern und werden hier nicht angezeigt.`}
												</p>
											)}

											{!detail.responses.length ? (
												<p className={styles.tabHint}>
													Noch keine Rückmeldungen.
												</p>
											) : (
												<ul className={styles.responseList}>
													{detail.responses.map(response => (
														<li
															key={response.id}
															className={styles.responseRow}
														>
															<div className={styles.responsePerson}>
																<span className={styles.responseName}>
																	{response.supporterName}
																</span>
																{response.supporterCompany && (
																	<span
																		className={
																			styles.responseCompany
																		}
																	>
																		{response.supporterCompany}
																	</span>
																)}
																{response.note && (
																	<p
																		className={
																			styles.responseNote
																		}
																	>
																		{response.note}
																	</p>
																)}
															</div>
															<span
																className={clsx(
																	styles.responseStatus,
																	styles[`response${response.status}`]
																)}
															>
																{response.status === 'ACCEPTED'
																	? 'Zusage'
																	: 'Absage'}
															</span>
														</li>
													))}
												</ul>
											)}
										</>
									))}

									{dialog.tab === 'history' && (
										<>
											{/*
											  * Erst die Kommentare, dann der Änderungsverlauf:
											  * beides in einem Reiter, damit es zu jedem Datensatz
											  * dieselbe eine Stelle gibt.
											  */}
											<RecordComments
												entityType={RECORD_TYPES.SUPPORT_REQUEST}
												entityId={draft.id}
											/>

											<h4 className={styles.historyTitle}>Änderungsverlauf</h4>
											<p className={styles.tabHint}>
												Der Verlauf beginnt mit seiner Einführung — was davor geändert
												wurde, ist nicht festgehalten. Der Text der Beschreibung steht
												bewusst nicht darin; vermerkt wird nur, dass er geändert wurde.
											</p>

											{historyFailed ? (
												<p className={styles.historyEmpty}>
													Der Verlauf lässt sich gerade nicht laden.
												</p>
											) : history === null ? (
												<Loading />
											) : !history.length ? (
												<p className={styles.historyEmpty}>
													Noch keine Änderungen festgehalten.
												</p>
											) : (
												<ul className={styles.history}>
													{history.map(entry => (
														<li key={entry.id} className={styles.historyItem}>
															<p className={styles.historyHead}>
																<span className={styles.historyTime}>
																	{formatDateTime(entry.createdAt)}
																</span>
																<span className={styles.historyWho}>
																	{entry.editorName || 'Unbekannt'}
																</span>
																<span className={styles.historyAction}>
																	{HISTORY_ACTION[entry.action] || entry.action}
																</span>
															</p>

															{entry.action === 'NOTIFIED' && (
																<p className={styles.historySent}>
																	An {entry.recipients ?? 0}{' '}
																	{entry.recipients === 1
																		? 'Unterstützer:in'
																		: 'Unterstützer:innen'}{' '}
																	gesendet
																</p>
															)}

															{!!entry.changes?.length && (
																<ul className={styles.historyChanges}>
																	{entry.changes.map((change, index) => (
																		<li key={`${change.field}-${index}`}>
																			<span className={styles.historyField}>
																				{HISTORY_LABEL[change.field] ||
																					change.field}
																			</span>
																			{'to' in change ? (
																				<>
																					<span
																						className={
																							styles.historyFrom
																						}
																					>
																						{historyValue(
																							change.field,
																							change.from
																						)}
																					</span>
																					<span
																						className={
																							styles.historyArrow
																						}
																					>
																						→
																					</span>
																					<span
																						className={styles.historyTo}
																					>
																						{historyValue(
																							change.field,
																							change.to
																						)}
																					</span>
																				</>
																			) : (
																				<span
																					className={styles.historyFrom}
																				>
																					geändert
																				</span>
																			)}
																		</li>
																	))}
																</ul>
															)}
														</li>
													))}
												</ul>
											)}
										</>
									)}
							</fieldset>
						</div>

						<footer className={styles.modalActions}>
							<Btn theme="secondary" onClick={closeDialog}>
								{/*
								 * Auf den Reitern Rückmeldungen und Verlauf gibt es nichts
								 * abzubrechen. "Schließen" hieße hier zweierlei - das ist
								 * auf dieser Seite die Aktion, die einen Bedarf beendet.
								 */}
								{isReadOnlyTab ? 'Fertig' : t('Cancel')}
							</Btn>
							{!isReadOnlyTab && (
								<Btn theme="primary" disabled={isSaving} onClick={save}>
									{t('save')}
								</Btn>
							)}
						</footer>
					</div>
				</div>
			)}
		</div>
	)
}
